import { useContext, useEffect, useState } from "react";
import { Subscription } from "../../../../../api/subscriptions";
import { SubscriptionsContext } from "../../../store/SubscriptionsContext";
import type { ModalConfig } from "../../../utils/types";
import "../../../modals/Modal.css";

export type EditSubscriptionModalBodyProps = {
  openMessage: (message: ModalConfig) => void;
  onClose: () => void;
  activeSubscription: Subscription;
};

export function EditSubscriptionModalBody({
  openMessage,
  onClose,
  activeSubscription,
}: EditSubscriptionModalBodyProps) {
  const { updateSubscription, deleteSubscription } =
    useContext(SubscriptionsContext);
  const [isCombo, setIsCombo] = useState(false);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [isDeleting, setIsDeleting] = useState(false);

  async function handleSubmit(event: React.FormEvent<HTMLFormElement>) {
    event?.preventDefault();

    if (isSubmitting) return;
    setIsSubmitting(true);

    const fd = new FormData(event.currentTarget);
    const data = Object.fromEntries(fd.entries());

    try {
      await updateSubscription(activeSubscription.id, {
        name: data.subscriptionType as string,
        spots: Number(data.availableSpots),
        isCombo: isCombo,
      } as Subscription);

      onClose();
      openMessage({
        title: "Updated Successfully!",
        body: activeSubscription.name
          ? `${activeSubscription.name} has been updated successfully!`
          : "Subscription was updated successfully!",
        showCloseButton: true,
      });
    } catch (err: unknown) {
      const msg =
        (err as { response?: { data?: { message?: string } } })?.response?.data
          ?.message ?? "Failed to update subscription.";
      onClose();
      openMessage({ body: msg });
    } finally {
      setIsSubmitting(false);
    }
  }

  async function handleDelete(id: string) {
    if (!window.confirm("Are you sure you want to delete this subscription?")) {
      return;
    }
    if (isDeleting) return;
    setIsDeleting(true);
    try {
      await deleteSubscription(id);
      onClose();
    } finally {
      setIsDeleting(false);
    }
  }

  useEffect(() => {
    if (activeSubscription) {
      if (activeSubscription.isCombo) {
        setIsCombo(activeSubscription.isCombo);
      }
    }
    return;
  }, [activeSubscription]);

  return (
    <div className="new-harvest-log-wrapper">
      <form onSubmit={handleSubmit}>
        <div className="modal-form-input-pair">
          <label htmlFor="subscription-type" className="sr-only">
            {activeSubscription.name}
          </label>
          <input
            type="text"
            name="subscriptionType"
            id="subscription-type"
            placeholder="Subscription Type"
            required
            defaultValue={activeSubscription.name}
          />
          {!isCombo && (
            <>
              <label htmlFor="available-spots" className="sr-only">
                Available Spots
              </label>
              <input
                type="number"
                name="availableSpots"
                id="available-spots"
                placeholder="Available Spots"
                required={!isCombo}
                defaultValue={
                  activeSubscription.spots ? activeSubscription.spots : ""
                }
              />
            </>
          )}
        </div>

        <div className="modal-form-input-pair-checkbox">
          <label>
            <input
              type="checkbox"
              name="comboCheckbox"
              checked={isCombo}
              onChange={(e) => setIsCombo(e.target.checked)}
            />
            This is a combo subscription.
          </label>
        </div>
        <div className="buttons-footer">
          <button type="button" className="btn equal-btns" onClick={onClose}>
            Cancel
          </button>
          <button
            type="submit"
            className="btn equal-btns"
            disabled={isSubmitting || isDeleting}
          >
            {isSubmitting ? "Saving..." : "Save"}
          </button>
        </div>

        <button
          className="btn delete-sub"
          type="button"
          disabled={isSubmitting || isDeleting}
          onClick={() => void handleDelete(activeSubscription.id)}
        >
          {isDeleting ? "Deleting..." : "Delete"}
        </button>

        <p>
          <span className="gray-text">* Tiers can be created after.</span>
        </p>
        <p>
          <span className="gray-text">
            * Combo subscriptions have no available spots themselves. They fetch
            availability from multiple other subscriptions. Ex: Flowers &
            Veggies take 1 spot from Veggies and 1 spot form Flowers
          </span>
        </p>
      </form>
    </div>
  );
}
